import { getCookie, hasCookie, setCookie } from 'cookies-next';
import { IncomingMessage, ServerResponse } from 'http';
import { ParsedUrlQuery } from 'querystring';

import { getAppEnv } from './getAppEnv';

export type Site = {
  enumerable: boolean;
  configurable: boolean;
  writable: boolean;
  value: string;
  coreSite: boolean;
};

const NEVER_EXPIRE = 2147483647;
const SALON_SLUG = 'salonSlug';

const hostNameIsStandardDomain = (hostname: string) =>
  hostname.endsWith('.glossgenius-qa.com') ||
  hostname.endsWith('.glossgenius.com') ||
  hostname.endsWith('.glossgenius-staging.com') ||
  hostname.endsWith('.localhost') ||
  hostname.endsWith('.lvh.me');

const cleanHostName = (hostname: string) => {
  if (hostname.startsWith('www.')) {
    return hostname.replace('www.', '');
  }
  if (hostname.includes(':')) {
    return hostname.substr(0, hostname.indexOf(':'));
  } else {
    return hostname;
  }
};

export function getSiteFromReq(
  req: IncomingMessage,
  res: ServerResponse,
  query: ParsedUrlQuery = {},
): Site {
  let xForwardedHost = req.headers['x-forwarded-host'];

  xForwardedHost = Array.isArray(xForwardedHost)
    ? xForwardedHost[0]
    : xForwardedHost;
  let hostname = xForwardedHost || req.headers.host || '';

  hostname = cleanHostName(hostname);

  let site: string;

  if (query.salonSlug && getAppEnv() !== 'prod') {
    /**
     * We support providing the salonSlug via query param
     * for preview deployments, because each build needs to
     * put the unique id in the subdomain.
     */
    site = Array.isArray(query.salonSlug)
      ? query.salonSlug[0]
      : query.salonSlug;
    setCookie(SALON_SLUG, site, { req, res, maxAge: NEVER_EXPIRE });
  } else if (hasCookie(SALON_SLUG, { req, res })) {
    site = getCookie(SALON_SLUG, { req, res }) as string;
  } else if (hostNameIsStandardDomain(hostname)) {
    // This is if the site is a standard domain
    site = hostname.substr(0, hostname.indexOf('.'));
  } else {
    // This is if the site is a custom domain
    const match = hostname.match(/(?![www.])[a-zA-Z0-9-]+\.\w+/i);

    if (match) {
      site = match[0] ? match[0] : 'www.glossgenius.com';
    } else {
      site = hostname;
    }
  }

  const coreSites = [
    'www.glossgenius.com',
    'glossgenius.com',
    'sola.glossgenius.com',
    'sola.lvh.me',
    'lvh.me',
    'localhost',
    'sola.glossgenius-staging.com',
    'www.glossgenius-staging.com',
    'glossgenius-staging.com',
    'sola.glossgenius-qa.com',
    'www.glossgenius-qa.com',
    'glossgenius-qa.com',
    'web-qa-v2.herokuapp.com',
    'core-web-landing-qa.herokuapp.com',
    '39b1d147dc5443138f2e6c139f9f2ac5.name',
    'www.39b1d147dc5443138f2e6c139f9f2ac5.name',
    'sola.39b1d147dc5443138f2e6c139f9f2ac5.name',
  ];

  return {
    enumerable: false,
    configurable: false,
    writable: false,
    value: site,
    coreSite: coreSites.indexOf(hostname) > -1,
  };
}
